// Client-side report export - both formats are generated entirely in the
// browser from data already fetched for the on-screen table, no new backend
// endpoints needed. CSV is a plain Blob-download (same pattern as the CSV
// import template download in CsvImportModal). "PDF" is the browser's own
// print-to-PDF via a printable HTML window - the same approach already used
// for asset QR labels (see lib/assetLabel.ts) - deliberately avoiding a new
// PDF-generation npm dependency for what's fundamentally a formatted table.

function csvEscape(value: string | number): string {
  const s = String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function downloadCsv(filename: string, headers: string[], rows: (string | number)[][]) {
  const lines = [headers, ...rows].map((row) => row.map(csvEscape).join(','));
  const csv = lines.join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function printReportAsPdf(title: string, headers: string[], rows: (string | number)[][], subtitle?: string) {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) {
    throw new Error("Could not open the print window - check your browser's pop-up blocker.");
  }

  const headerHtml = headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('');
  const rowsHtml = rows
    .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(String(cell))}</td>`).join('')}</tr>`)
    .join('');

  win.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(title)}</title>
        <style>
          body { font-family: -apple-system, Segoe UI, Arial, sans-serif; padding: 32px; color: #0f172a; }
          h1 { font-size: 18px; margin: 0 0 4px; }
          p.subtitle { font-size: 12px; color: #64748b; margin: 0 0 20px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { text-align: left; padding: 6px 10px; border-bottom: 1px solid #e2e8f0; }
          th { text-transform: uppercase; font-size: 10px; color: #64748b; letter-spacing: 0.04em; }
          p.generated { margin-top: 20px; font-size: 10px; color: #94a3b8; }
          @media print { body { padding: 12px; } }
        </style>
      </head>
      <body onload="window.print()">
        <h1>${escapeHtml(title)}</h1>
        ${subtitle ? `<p class="subtitle">${escapeHtml(subtitle)}</p>` : ''}
        <table>
          <thead><tr>${headerHtml}</tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
        <p class="generated">Generated ${new Date().toLocaleString('en-GB')} - ATLAS Asset Portal</p>
      </body>
    </html>
  `);
  win.document.close();
}
